import Cookie from 'js-cookie';

/**
 * GA4 helpers — the .in engine's @common/utils/ga.js, same exports, same
 * event shape, vanilla instead of cash-dom like the rest of @common.
 *
 * THE SHAPE IS .in's, NOT GA4's "recommended events": each gaSC(category,
 * label) sends an event NAMED after the category, carrying event_category /
 * event_label / value — and, first, a page_view carrying the custom
 * dimensions (page type, user id, coupon id, position). That is how every
 * report on the .in property is built, and the brief for International is to
 * match it.
 *
 * The layout defines window.gtag only when GA_MEASUREMENT_ID is configured.
 * When it is not (stage, laptops) every call here is a no-op, which is why
 * nothing else ever checks for it.
 */

const ready = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/**
 * The signed-in visitor's id for dimension2. .in reads its `siod` session
 * cookie; International's login stores the user object in `user`, so the id
 * comes out of that. Empty for a guest.
 */
function userId() {
  try {
    const raw = Cookie.get('user');
    if (!raw) return '';
    const user = JSON.parse(raw);
    return String(user.id || user.userId || user.email || '');
  } catch {
    return '';
  }
}

/**
 * The dimensions page_view: page type, user, coupon, position. .in sends one
 * before EVERY event so the event that follows can be sliced by them.
 */
export function getGAPageObject(cid, position) {
  if (!ready()) return;
  const dimensions = {
    dimension1: (window.goGlobal && window.goGlobal.PageType) || '',
    dimension2: userId(),
    dimension3: cid || '',
  };
  if (position) dimensions.dimension4 = position;
  window.gtag('event', 'page_view', dimensions);
}

/**
 * The event helper every page uses — .in's signature exactly:
 * gaSC(eventCategory, eventLabel, eventValue, couponid).
 */
export default function gaSC(eventCategory, eventLabel, eventValue, couponid) {
  try {
    if (!ready()) return;
    getGAPageObject(couponid);
    window.gtag('event', eventCategory, {
      event_category: eventCategory,
      event_label: eventLabel || '',
      value: eventValue || 0,
    });
  } catch {
    /* analytics must never break behaviour */
  }
}

/** .in's gaDT: the ACTION is the event name, and the position rides as value. */
export function gaDT(eventCategory, eventAction, eventLabel, eventValue, couponid) {
  try {
    if (!ready()) return;
    getGAPageObject(couponid, eventValue || 0);
    window.gtag('event', eventAction, {
      event_category: eventAction,
      event_label: eventLabel || '',
      value: eventValue || 0,
    });
  } catch {
    /* no-op */
  }
}

/** Named event with an arbitrary payload — kept for the few callers that use it. */
export function trackEvent(name, params) {
  try {
    if (!ready()) return;
    window.gtag('event', name, params || {});
  } catch {
    /* no-op */
  }
}

const root = () => document.getElementById('gBody') || document.body;

/**
 * One event per click matching `selector`, DELEGATED from #gBody — the
 * category page rewrites its coupon list on tab switch and "show more", so a
 * handler bound to the elements themselves would miss everything that arrived
 * after load. .in keeps npga (delegated) and npga2 (direct) apart because a
 * delegated selector that also matches something inside a card double-counts.
 */
export function npga(selector, categoryName) {
  const el = root();
  if (!el) return;
  el.addEventListener('click', (event) => {
    if (event.target.closest(selector)) gaSC(categoryName);
  });
}

export function npga2(selector, categoryName) {
  document.querySelectorAll(selector).forEach((node) => {
    node.addEventListener('click', () => gaSC(categoryName));
  });
}

/** Position-based: the element's data-pos becomes the label. Delegated. */
export function pbGA(selector, categoryName) {
  const el = root();
  if (!el) return;
  el.addEventListener('click', (event) => {
    const hit = event.target.closest(selector);
    if (hit) gaSC(categoryName, hit.getAttribute('data-pos') || '');
  });
}

/** Position-based, bound directly. */
export function pbGA2(selector, categoryName) {
  document.querySelectorAll(selector).forEach((node) => {
    node.addEventListener('click', () => gaSC(categoryName, node.getAttribute('data-pos') || ''));
  });
}

export function dtGA(selector, eventCategory, eventAction, eventLabel) {
  const el = root();
  if (!el) return;
  el.addEventListener('click', (event) => {
    const hit = event.target.closest(selector);
    if (hit) gaDT(eventCategory, eventAction, eventLabel, hit.getAttribute('data-pos') || '');
  });
}

export function dtGAP(selector, eventCategory, eventAction, eventLabel, position) {
  const el = root();
  if (!el) return;
  el.addEventListener('click', (event) => {
    if (event.target.closest(selector)) gaDT(eventCategory, eventAction, eventLabel, position);
  });
}

export function dtftGA(selector, eventCategory, eventAction) {
  const el = root();
  if (!el) return;
  el.addEventListener('click', (event) => {
    const hit = event.target.closest(selector);
    if (!hit) return;
    gaDT(eventCategory, eventAction, hit.getAttribute('data-fn') || 'all', hit.getAttribute('data-pos') || '');
  });
}

// .in exposes it globally for inline onclick handlers in migrated markup.
if (typeof window !== 'undefined') window.gaSC = gaSC;
